<template>
  <div>
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#68D389" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left align-self-center>{{ msg }}</v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-layout wrap>
      <v-flex xs12>
        <v-layout wrap align-center py-4>
          <v-flex xs12 text-left>
            <span style="font-family: poppinsbold; font-size: 20px">Refund Eligibility Check</span>
          </v-flex>
          <v-flex xs12 text-left>
            <span style="font-family: poppinsregular; font-size: 13px; color:#777">
              Enter a Razorpay Order ID (order_...) or Payment ID (pay_...) to see its age
              and whether a refund can still be processed. This is read-only — it never refunds.
            </span>
          </v-flex>
        </v-layout>

        <v-layout wrap align-center pb-4>
          <v-flex xs12 sm8 md6 pr-sm-2>
            <v-text-field
              v-model="orderId"
              label="Order ID or Payment ID"
              placeholder="order_XXXXXXXXXXXX"
              outlined
              dense
              hide-details
              @keyup.enter="check()"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 md3 pt-2 pt-sm-0 text-left>
            <v-btn depressed color="#68D389" dark :loading="loading" @click="check()">
              <v-icon left small>mdi-magnify</v-icon>Check
            </v-btn>
          </v-flex>
        </v-layout>

        <v-divider class="my-2"></v-divider>

        <v-layout wrap v-if="result">
          <v-flex xs12 py-3 v-if="result.order">
            <v-card outlined class="pa-3">
              <div style="font-family: poppinsbold; font-size: 16px">Order {{ result.order.id }}</div>
              <v-layout wrap pt-2>
                <v-flex xs6 sm3><span class="lbl">Status</span><br />{{ result.order.status }}</v-flex>
                <v-flex xs6 sm3><span class="lbl">Order Amount</span><br />₹ {{ result.order.amount }}</v-flex>
                <v-flex xs6 sm3><span class="lbl">Amount Paid</span><br />₹ {{ result.order.amountPaid }}</v-flex>
                <v-flex xs6 sm3><span class="lbl">Created</span><br />{{ formatDate(result.order.createdAt) }}</v-flex>
              </v-layout>
            </v-card>
          </v-flex>

          <v-flex xs12 py-2 v-for="(p, i) in result.payments" :key="i">
            <v-card
              outlined
              class="pa-3"
              :style="'border-left: 6px solid ' + (p.withinRefundWindow ? '#2E7D32' : '#C62828')"
            >
              <v-layout wrap align-center>
                <v-flex xs12 sm8>
                  <div style="font-family: poppinsbold; font-size: 15px">Payment {{ p.paymentId }}</div>
                </v-flex>
                <v-flex xs12 sm4 text-sm-right>
                  <v-chip small dark :color="p.withinRefundWindow ? '#2E7D32' : '#C62828'">
                    {{ p.withinRefundWindow ? 'Within refund window' : 'Past refund window' }}
                  </v-chip>
                </v-flex>
              </v-layout>

              <v-layout wrap pt-3>
                <v-flex xs6 sm3 pb-2><span class="lbl">Status</span><br />{{ p.status }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Method</span><br />{{ p.method || "-" }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Amount</span><br />₹ {{ p.amount }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Already Refunded</span><br />₹ {{ p.amountRefunded }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Refund Status</span><br />{{ p.refundStatus || "-" }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Captured</span><br />{{ p.captured ? "Yes" : "No" }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Created</span><br />{{ formatDate(p.createdAt) }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Age</span><br />{{ p.ageDays }} days</v-flex>
              </v-layout>

              <v-layout wrap pt-2 v-if="p.errorDescription">
                <v-flex xs12><span class="lbl">Error</span><br />{{ p.errorDescription }}</v-flex>
              </v-layout>

              <v-layout wrap pt-3>
                <v-flex xs12>
                  <v-alert
                    dense
                    text
                    :type="p.withinRefundWindow ? 'success' : 'error'"
                    class="mb-0"
                  >
                    <span v-if="p.withinRefundWindow">
                      This payment is {{ p.ageDays }} days old (within ~180 days). A refund to the
                      original source can normally still be processed in the Razorpay Dashboard.
                    </span>
                    <span v-else>
                      This payment is {{ p.ageDays }} days old (beyond Razorpay's ~180-day window).
                      Razorpay will likely reject a refund to the original source — refund the guest
                      manually (bank transfer / UPI) and then mark the cancellation Completed.
                    </span>
                  </v-alert>
                </v-flex>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout wrap v-else-if="searched">
          <v-flex xs12 text-left pa-5>
            <span style="font-family:poppinsregular">No details found for that id.</span>
          </v-flex>
        </v-layout>

        <v-divider class="my-6"></v-divider>

        <!-- Booking origin: when & how a ticket was booked (from the app DB) -->
        <v-layout wrap align-center py-2>
          <v-flex xs12 text-left>
            <span style="font-family: poppinsbold; font-size: 20px">How was this ticket booked?</span>
          </v-flex>
          <v-flex xs12 text-left>
            <span style="font-family: poppinsregular; font-size: 13px; color:#777">
              Enter a Ticket No to see when it was booked, whether it was an Online or Counter
              booking, and how far ahead the program date was. Works even for old orders.
            </span>
          </v-flex>
        </v-layout>

        <v-layout wrap align-center pb-4>
          <v-flex xs12 sm8 md6 pr-sm-2>
            <v-text-field
              v-model="ticketNo"
              label="Ticket No"
              placeholder="14499"
              outlined
              dense
              hide-details
              @keyup.enter="checkOrigin()"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4 md3 pt-2 pt-sm-0 text-left>
            <v-btn depressed color="#68D389" dark :loading="originLoading" @click="checkOrigin()">
              <v-icon left small>mdi-history</v-icon>Check
            </v-btn>
          </v-flex>
        </v-layout>

        <v-layout wrap v-if="origin">
          <v-flex xs12 py-2>
            <v-card
              outlined
              class="pa-3"
              :style="'border-left: 6px solid ' + (origin.exceedsNormalWindow || origin.bookingType === 'Counter' ? '#C62828' : '#2E7D32')"
            >
              <div style="font-family: poppinsbold; font-size: 16px">Ticket #{{ origin.ticketNo }}</div>
              <v-layout wrap pt-3>
                <v-flex xs6 sm3 pb-2><span class="lbl">Booked On</span><br />{{ formatDate(origin.bookedOn) }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Booking Type</span><br />{{ origin.bookingType }}</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Booked Ahead</span><br />{{ origin.maxAdvanceDays }} days</v-flex>
                <v-flex xs6 sm3 pb-2><span class="lbl">Normal Window</span><br />{{ origin.normalWindowDays }} days</v-flex>
                <v-flex xs6 sm3 pb-2 v-if="origin.offlineId"><span class="lbl">Offline ID</span><br />{{ origin.offlineId }}</v-flex>
                <v-flex xs6 sm3 pb-2 v-if="origin.orderId"><span class="lbl">Order ID</span><br />{{ origin.orderId }}</v-flex>
              </v-layout>

              <v-simple-table dense class="mt-2">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Programme</th>
                      <th class="text-left">Program Date</th>
                      <th class="text-left">Booked Ahead</th>
                      <th class="text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(it, i) in origin.items" :key="i">
                      <td class="text-left">{{ it.programme }}<span v-if="it.isCottage"> (Accommodation)</span></td>
                      <td class="text-left">{{ formatDate(it.programDate) }}</td>
                      <td class="text-left">{{ it.advanceDays }} days</td>
                      <td class="text-left">{{ it.status }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-layout wrap v-if="origin.wasRescheduled" pt-3>
                <v-flex xs12>
                  <div style="font-family: poppinsbold; font-size: 14px; color:#C62828">
                    Reschedule history ({{ origin.reschedules.length }})
                  </div>
                  <v-simple-table dense class="mt-1">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Moved From</th>
                          <th class="text-left">Moved To</th>
                          <th class="text-left">On</th>
                          <th class="text-left">Done By</th>
                          <th class="text-left">Extra Paid</th>
                          <th class="text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(r, i) in origin.reschedules" :key="i">
                          <td class="text-left">{{ formatDate(r.fromDate) }}</td>
                          <td class="text-left">{{ formatDate(r.toDate) }}</td>
                          <td class="text-left">{{ formatDate(r.on) }}</td>
                          <td class="text-left">
                            {{ r.doneBy }}
                            <span v-if="r.doneByDetail" style="color:#999; font-size:11px"><br />{{ r.doneByDetail }}</span>
                          </td>
                          <td class="text-left">₹ {{ r.additionalPaymentAmount }}</td>
                          <td class="text-left">{{ r.status }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-flex>
              </v-layout>

              <v-layout wrap v-if="origin.roomBookings && origin.roomBookings.length" pt-3>
                <v-flex xs12>
                  <div style="font-family: poppinsbold; font-size: 14px; color:#1B5E20">
                    Room bookings by date (total room allocations: {{ origin.totalRoomAllocations }})
                  </div>
                  <v-simple-table dense class="mt-1">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Date</th>
                          <th class="text-left">Programme</th>
                          <th class="text-left">Rooms this date</th>
                          <th class="text-left">Guests in rooms</th>
                          <th class="text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(rb, i) in origin.roomBookings" :key="i">
                          <td class="text-left">{{ formatDate(rb.date) }}</td>
                          <td class="text-left">{{ rb.programme }}</td>
                          <td class="text-left">{{ rb.allocationCount }}</td>
                          <td class="text-left">{{ rb.guestsInRooms }}</td>
                          <td class="text-left">{{ rb.status }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <div style="font-family: poppinsregular; font-size: 12px; color:#777" class="mt-1">
                    A report that shows a single total adds these up — e.g. 3 rooms on two
                    dates appears as 6. The "Rooms this date" column is the real per-night count.
                  </div>
                </v-flex>
              </v-layout>

              <v-layout wrap v-if="origin.cancels && origin.cancels.length" pt-3>
                <v-flex xs12>
                  <div style="font-family: poppinsbold; font-size: 14px; color:#C62828">
                    Cancellation / refund records ({{ origin.cancels.length }})
                  </div>
                  <v-simple-table dense class="mt-1">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Cancelled Program Date(s)</th>
                          <th class="text-left">Refund Amount</th>
                          <th class="text-left">Status</th>
                          <th class="text-left">Initiated</th>
                          <th class="text-left">Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(cc, i) in origin.cancels" :key="i">
                          <td class="text-left">
                            <span v-if="cc.cancelledPrograms && cc.cancelledPrograms.length">
                              <span v-for="(cp, j) in cc.cancelledPrograms" :key="j">
                                {{ cp.programme }} — {{ formatDate(cp.programDate) }}<br />
                              </span>
                            </span>
                            <span v-else>-</span>
                          </td>
                          <td class="text-left">₹ {{ cc.refundAmount }}</td>
                          <td class="text-left">{{ cc.status }}</td>
                          <td class="text-left">{{ formatDate(cc.initDate) }}</td>
                          <td class="text-left">{{ formatDate(cc.completedDate) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-flex>
              </v-layout>

              <v-layout wrap pt-2 v-if="origin.ticketDocCount > 1">
                <v-flex xs12>
                  <v-alert dense text type="info" class="mb-0">
                    This ticket number maps to {{ origin.ticketDocCount }} ticket records
                    ({{ origin.paymentItemCount }} bookings) — the dates above cover all of them.
                  </v-alert>
                </v-flex>
              </v-layout>

              <v-alert
                dense
                text
                class="mb-0 mt-3"
                :type="origin.wasRescheduled || origin.exceedsNormalWindow || origin.bookingType === 'Counter' ? 'warning' : 'success'"
              >
                {{ origin.verdict }}
              </v-alert>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout wrap v-else-if="originSearched">
          <v-flex xs12 text-left pa-5>
            <span style="font-family:poppinsregular">No booking data found for that ticket.</span>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import axios from "axios";
export default {
  data() {
    return {
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      orderId: "",
      loading: false,
      searched: false,
      result: null,
      ticketNo: "",
      originLoading: false,
      originSearched: false,
      origin: null,
    };
  },
  methods: {
    checkOrigin() {
      if (!this.ticketNo) {
        this.msg = "Please enter a Ticket No.";
        this.showSnackBar = true;
        return;
      }
      this.originLoading = true;
      this.origin = null;
      axios({
        method: "GET",
        url: "booking/bookingorigin",
        params: { ticketNo: this.ticketNo.toString().trim() },
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.originLoading = false;
          this.originSearched = true;
          if (response.data.status) {
            this.origin = response.data;
          } else {
            this.origin = null;
            this.msg = response.data.msg || "Could not fetch booking origin.";
            this.showSnackBar = true;
          }
        })
        .catch(() => {
          this.originLoading = false;
          this.ServerError = true;
        });
    },
    check() {
      if (!this.orderId) {
        this.msg = "Please enter an Order ID or Payment ID.";
        this.showSnackBar = true;
        return;
      }
      this.loading = true;
      this.result = null;
      axios({
        method: "GET",
        url: "cancel/razorpay/inspect",
        params: { id: this.orderId.trim() },
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.loading = false;
          this.searched = true;
          if (response.data.status) {
            this.result = response.data;
          } else {
            this.result = null;
            this.msg = response.data.msg || "Could not fetch details.";
            this.showSnackBar = true;
          }
        })
        .catch(() => {
          this.loading = false;
          this.ServerError = true;
        });
    },
    formatDate(item) {
      if (!item) return "-";
      var dt = new Date(item);
      var day = ("0" + dt.getDate()).slice(-2);
      var month = ("0" + (dt.getMonth() + 1)).slice(-2);
      return day + "-" + month + "-" + dt.getFullYear();
    },
  },
};
</script>

<style scoped>
.lbl {
  font-family: poppinsregular;
  font-size: 12px;
  color: #999;
}
</style>
